import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  ChevronUp,
  ChevronDown,
  Clock,
  Calendar,
  AlertCircle,
  Activity,
  BarChart2,
  RefreshCw,
  Loader,
  Search,
  Bell,
  Eye,
  EyeOff,
  BookmarkPlus,
  Filter,
  Target,
  Zap,
  PieChart as PieChartIcon,
  ArrowUp,
  ArrowDown,
  Minus,
  Info,
  Star,
  Settings,
  Download,
  X,
} from "lucide-react";
import adminProductService from "@/services/api/admin.product.service";

const EnhancedStockDashboard = () => {
  const [selectedStock, setSelectedStock] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const [stockHistory, setStockHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(false); // Separate loading state for chart
  const [error, setError] = useState(null);
  const [chartType, setChartType] = useState("line");
  const [timeFrame, setTimeFrame] = useState("1m");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // New advanced filters
  const [filters, setFilters] = useState({
    sector: "all",
    industry: "all",
    minPrice: "",
    maxPrice: "",
    trend: "all",
    priceRange: "all",
    sortBy: "percentage",
    sortOrder: "desc",
    includeMetrics: true,
  });

  const [showFilters, setShowFilters] = useState(false);
  const [marketMetrics, setMarketMetrics] = useState(null);
  const [sectorPerformance, setSectorPerformance] = useState([]);
  const [industryPerformance, setIndustryPerformance] = useState([]);

  // Enhanced market data fetching with advanced parameters
  const fetchMarketData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const options = {
        limit: 50,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        includeMetrics: filters.includeMetrics,
      };

      // Add filters only if they're not 'all'
      if (filters.sector !== "all") options.sector = filters.sector;
      if (filters.industry !== "all") options.industry = filters.industry;
      if (filters.trend !== "all") options.trend = filters.trend;
      if (filters.priceRange !== "all") options.priceRange = filters.priceRange;
      if (filters.minPrice) options.minPrice = parseFloat(filters.minPrice);
      if (filters.maxPrice) options.maxPrice = parseFloat(filters.maxPrice);

      const response = await adminProductService.getMarketOverview(options);

      if (response.success && response.data) {
        setMarketData(response.data);
        setMarketMetrics(response.data.summary);

        // Process sector and industry performance
        if (response.data.sectorBreakdown) {
          const sectorData = Object.entries(response.data.sectorBreakdown).map(
            ([sector, data]) => ({
              name: sector,
              value: data.averagePerformance || 0,
              count: data.count || 0,
              color: getSectorColor(sector),
            })
          );
          setSectorPerformance(sectorData);
        }

        if (response.data.industryBreakdown) {
          const industryData = Object.entries(response.data.industryBreakdown)
            .slice(0, 10)
            .map(([industry, data]) => ({
              name: industry,
              value: data.averagePerformance || 0,
              count: data.count || 0,
            }));
          setIndustryPerformance(industryData);
        }

        // Auto-select first stock if available and none selected
        if (response.data.stocks?.length > 0 && !selectedStock) {
          setSelectedStock(response.data.stocks[0]);
        }
      } else {
        throw new Error("Invalid market data response");
      }
    } catch (err) {
      setError(`Failed to load market data: ${err.message}`);
      console.error("Error fetching market data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [filters, selectedStock]);

  // FIXED: Enhanced fetchStockHistory function with proper dependency management
  const fetchStockHistory = useCallback(
    async (productId, forceRefresh = false) => {
      if (!productId) {
      
        return;
      }

      // Don't fetch if we're already loading the same stock (unless forced)
      if (isChartLoading && !forceRefresh) {
      
        return;
      }

     
      setIsChartLoading(true);
      setError(null);

      try {
        const response = await adminProductService.getStockChartData(
          productId,
          timeFrame,
          chartType
        );

        if (response.success && response.data) {
          const apiData = response.data;
   

          // CRITICAL: Proper data transformation
          const rawChartData = apiData.chartData || [];

          if (rawChartData.length === 0) {
           

            setStockHistory([]);
            return;
          }

          // Enhanced data processing with validation
          const chartData = rawChartData.map((item, index) => {
            // Validate required fields
            const open = parseFloat(item.open) || 0;
            const high = parseFloat(item.high) || open;
            const low = parseFloat(item.low) || open;
            const close = parseFloat(item.close) || open;
            const volume = parseInt(item.volume) || 0;

            return {
              // Time data
              date:
                item.date ||
                new Date(item.timestamp).toISOString().split("T")[0],
              timestamp: item.timestamp,
              datetime: item.datetime,

              // OHLC data with validation
              open: open,
              high: Math.max(high, open, close), // Ensure high >= open,close
              low: Math.min(low, open, close), // Ensure low <= open,close
              close: close,

              // Price data
              buyPrice: parseFloat(item.buyPrice) || open,
              sellPrice: parseFloat(item.sellPrice) || close,

              // Volume and metadata
              volume: volume,
              colorCoding: item.colorCoding || "neutral",
              snapshotType: item.snapshotType || "1m",

              // Technical indicators
              rsi: calculateRSI(rawChartData.slice(0, index + 1)),
              momentum: calculateMomentum(rawChartData.slice(0, index + 1)),

              // Derived fields for charts
              change:
                index > 0
                  ? close - parseFloat(rawChartData[index - 1].close || 0)
                  : 0,
              changePercent:
                index > 0
                  ? ((close - parseFloat(rawChartData[index - 1].close || 0)) /
                      parseFloat(rawChartData[index - 1].close || 1)) *
                    100
                  : 0,
            };
          });

          // IMPORTANT: Sort by timestamp to ensure chronological order
          chartData.sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          );

          // FIXED: Update selected stock with current metrics using functional update
          if (apiData.metrics) {
            setSelectedStock((prevSelectedStock) => {
              // Only update if this is for the currently selected stock
              if (
                prevSelectedStock &&
                prevSelectedStock.productId === productId
              ) {
                return {
                  ...prevSelectedStock,
                  currentPrice:
                    apiData.metrics.currentPrice ||
                    prevSelectedStock.currentPrice,
                  dailyChange:
                    apiData.metrics.dailyChange ||
                    prevSelectedStock.dailyChange,
                  dailyPercentage:
                    apiData.metrics.dailyPercentage ||
                    prevSelectedStock.dailyPercentage,
                  periodChange:
                    apiData.metrics.periodChange ||
                    prevSelectedStock.periodChange,
                  periodPercentage:
                    apiData.metrics.periodPercentage ||
                    prevSelectedStock.periodPercentage,
                  high52Week:
                    apiData.metrics.high52Week || prevSelectedStock.high52Week,
                  low52Week:
                    apiData.metrics.low52Week || prevSelectedStock.low52Week,
                  volume: apiData.metrics.volume || prevSelectedStock.volume,
                  // Additional metrics from API
                  periodHigh:
                    apiData.metrics.periodHigh || prevSelectedStock.periodHigh,
                  periodLow:
                    apiData.metrics.periodLow || prevSelectedStock.periodLow,
                  position52Week:
                    apiData.metrics.position52Week ||
                    prevSelectedStock.position52Week,
                  rsi: apiData.metrics.rsi || prevSelectedStock.rsi,
                  volatility:
                    apiData.metrics.volatility || prevSelectedStock.volatility,
                  momentum:
                    apiData.metrics.momentum || prevSelectedStock.momentum,
                };
              }
              return prevSelectedStock;
            });
          }

          setStockHistory(chartData);

        
        } else {
          throw new Error("Invalid chart data response structure");
        }
      } catch (err) {
        console.error("❌ Error fetching stock history:", err);
        setError(`Failed to load stock history: ${err.message}`);
        setStockHistory([]);
      } finally {
        setIsChartLoading(false);
      }
    },
    [timeFrame, chartType] // FIXED: Removed selectedStock from dependencies
  );

  // Enhanced technical indicator calculations
  const calculateRSI = (data, period = 14) => {
    if (data.length < period + 1) return 50;

    const closes = data.slice(-period - 1).map((d) => parseFloat(d.close) || 0);
    const gains = [];
    const losses = [];

    for (let i = 1; i < closes.length; i++) {
      const change = closes[i] - closes[i - 1];
      gains.push(change > 0 ? change : 0);
      losses.push(change < 0 ? Math.abs(change) : 0);
    }

    const avgGain = gains.reduce((a, b) => a + b, 0) / gains.length;
    const avgLoss = losses.reduce((a, b) => a + b, 0) / losses.length;

    if (avgLoss === 0) return 100;

    const rs = avgGain / avgLoss;
    return 100 - 100 / (1 + rs);
  };

  const calculateMomentum = (data) => {
    if (data.length < 2) return 0;

    const current = parseFloat(data[data.length - 1].close) || 0;
    const previous = parseFloat(data[0].close) || 0;

    return previous > 0 ? ((current - previous) / previous) * 100 : 0;
  };

  // Enhanced search with real API
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await adminProductService.searchStocks(query, 15);

      if (response.success && response.data) {
        setSearchResults(response.data.stocks || response.data);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error("Error searching stocks:", err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // FIXED: Enhanced stock selection handler
  const handleStockSelection = useCallback(
    (stock) => {


      // Clear previous chart data immediately
      setStockHistory([]);

      // Update selected stock
      setSelectedStock(stock);

      // Force fetch new chart data
      if (stock?.productId) {
        fetchStockHistory(stock.productId, true);
      }
    },
    [fetchStockHistory]
  );

  // Utility functions
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatLargeNumber = (value) => {
    if (value >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `${(value / 100000).toFixed(2)} L`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value?.toString() || "0";
  };

  const getSectorColor = (sector) => {
    const colors = {
      Technology: "#3B82F6",
      Healthcare: "#10B981",
      Finance: "#F59E0B",
      Energy: "#EF4444",
      Consumer: "#8B5CF6",
      Industrial: "#6B7280",
      Materials: "#84CC16",
      Utilities: "#06B6D4",
      Telecom: "#F97316",
      "Real Estate": "#EC4899",
    };
    return colors[sector] || "#9CA3AF";
  };

  const getTrendIcon = (trend, size = 16) => {
    switch (trend) {
      case "bullish":
        return (
          <TrendingUp
            className={`text-green-500 w-${size / 4} h-${size / 4}`}
          />
        );
      case "bearish":
        return (
          <TrendingDown
            className={`text-red-500 w-${size / 4} h-${size / 4}`}
          />
        );
      default:
        return (
          <Minus className={`text-gray-500 w-${size / 4} h-${size / 4}`} />
        );
    }
  };

  const getTrendStrengthColor = (strength) => {
    if (strength >= 70) return "text-green-600";
    if (strength >= 40) return "text-yellow-600";
    return "text-red-600";
  };

  // Memoized filtered stocks
  const displayStocks = useMemo(() => {
    const stocks = searchTerm.trim() ? searchResults : marketData?.stocks || [];

    return stocks.filter((stock) => {
      const matchesSearch =
        !searchTerm.trim() ||
        stock.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stock.symbol?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });
  }, [searchTerm, searchResults, marketData]);

  // Effects
  useEffect(() => {
    fetchMarketData();
  }, [fetchMarketData]);

  // FIXED: Separate effect for handling stock changes
  useEffect(() => {
    if (selectedStock?.productId) {
      fetchStockHistory(selectedStock.productId, true);
    }
  }, [selectedStock?.productId, fetchStockHistory]);

  // FIXED: Separate effect for chart type/timeframe changes
  useEffect(() => {
    if (selectedStock?.productId) {

      fetchStockHistory(selectedStock.productId, true);
    }
  }, [timeFrame, chartType]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      handleSearch(searchTerm);
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, handleSearch]);

  const renderAdvancedChart = () => {
    if (!stockHistory || stockHistory.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          <div className="text-center">
            <BarChart2 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>No chart data available</p>
          </div>
        </div>
      );
    }

    const commonProps = {
      data: stockHistory,
      margin: { top: 20, right: 30, left: 20, bottom: 20 },
    };

    const chartContent = (
      <>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="date"
          tickFormatter={(date) => {
            try {
              const d = new Date(date);
              return d.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
              });
            } catch (e) {
              return date;
            }
          }}
          stroke="#9ca3af"
          fontSize={12}
        />
        <YAxis
          stroke="#9ca3af"
          domain={["auto", "auto"]}
          tickFormatter={(value) => `₹${Math.round(value)}`}
          fontSize={12}
        />
        <Tooltip content={<AdvancedTooltip />} />
        <Legend />
      </>
    );

    // Handle different chart types based on your API data
    if (chartType === "area") {
      return (
        <AreaChart {...commonProps}>
          {chartContent}
          <Area
            type="monotone"
            dataKey="close"
            stroke="#3b82f6"
            fill="#3b82f6"
            fillOpacity={0.3}
            strokeWidth={2}
            name="Close Price"
          />
        </AreaChart>
      );
    }

    if (chartType === "candlestick") {
      return (
        <ComposedChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            tickFormatter={(date) => {
              try {
                const d = new Date(date);
                return d.toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                });
              } catch (e) {
                return date;
              }
            }}
            stroke="#9ca3af"
            fontSize={12}
          />
          {/* Primary Y-axis for price data */}
          <YAxis
            yAxisId="price"
            stroke="#9ca3af"
            domain={["auto", "auto"]}
            tickFormatter={(value) => `₹${Math.round(value)}`}
            fontSize={12}
          />
          {/* Secondary Y-axis for volume data */}
          <YAxis
            yAxisId="volume"
            orientation="right"
            stroke="#8884d8"
            tickFormatter={(value) => formatLargeNumber(value)}
            fontSize={12}
          />
          <Tooltip content={<AdvancedTooltip />} />
          <Legend />

          {/* Price lines with yAxisId */}
          <Line
            yAxisId="price"
            type="monotone"
            dataKey="high"
            stroke="#10b981"
            strokeWidth={1}
            dot={false}
            name="High"
          />
          <Line
            yAxisId="price"
            type="monotone"
            dataKey="low"
            stroke="#ef4444"
            strokeWidth={1}
            dot={false}
            name="Low"
          />
          <Line
            yAxisId="price"
            type="monotone"
            dataKey="open"
            stroke="#f59e0b"
            strokeWidth={1}
            dot={false}
            name="Open"
          />
          <Line
            yAxisId="price"
            type="monotone"
            dataKey="close"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
            name="Close"
          />

          {/* Volume bars with matching yAxisId */}
          <Bar
            yAxisId="volume"
            dataKey="volume"
            fill="#8884d8"
            fillOpacity={0.3}
            name="Volume"
          />
        </ComposedChart>
      );
    }

    // Default line chart
    return (
      <LineChart {...commonProps}>
        {chartContent}
        <Line
          type="monotone"
          dataKey="close"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={false}
          name="Close Price"
        />
      </LineChart>
    );
  };

  const AdvancedTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <div className="bg-white p-4 border border-gray-200 shadow-lg rounded-lg">
          <p className="font-semibold text-gray-800 mb-2">
            {new Date(data?.datetime || data?.date).toLocaleString()}
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <span className="text-sm text-gray-600">Open:</span>
            <span className="text-sm font-medium">
              ₹{data?.open?.toFixed(2) || "N/A"}
            </span>
            <span className="text-sm text-gray-600">High:</span>
            <span className="text-sm font-medium">
              ₹{data?.high?.toFixed(2) || "N/A"}
            </span>
            <span className="text-sm text-gray-600">Low:</span>
            <span className="text-sm font-medium">
              ₹{data?.low?.toFixed(2) || "N/A"}
            </span>
            <span className="text-sm text-gray-600">Close:</span>
            <span className="text-sm font-medium">
              ₹{data?.close?.toFixed(2) || "N/A"}
            </span>
            <span className="text-sm text-gray-600">Buy Price:</span>
            <span className="text-sm font-medium">
              ₹{data?.buyPrice?.toFixed(2) || "N/A"}
            </span>
            <span className="text-sm text-gray-600">Volume:</span>
            <span className="text-sm font-medium">
              {formatLargeNumber(data?.volume)}
            </span>
            <span className="text-sm text-gray-600">Type:</span>
            <span className="text-sm font-medium">
              {data?.snapshotType || "N/A"}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  if (isLoading && !marketData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
          <Loader
            className="animate-spin text-blue-500 mb-4 mx-auto"
            size={48}
          />
          <p className="text-xl font-semibold text-gray-700">
            Loading Advanced Market Data...
          </p>
          <p className="text-gray-500 mt-2">
            Analyzing trends, volatility, and market metrics
          </p>
        </div>
      </div>
    );
  }

  if (error && !marketData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
          <AlertCircle className="text-red-500 mb-4 mx-auto" size={48} />
          <p className="text-xl font-semibold text-red-600">
            Error Loading Data
          </p>
          <p className="text-gray-600 mt-2">{error}</p>
          <button
            onClick={fetchMarketData}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Enhanced Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Advanced Stock Analytics
              </h1>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">Live Market Data</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Enhanced Market Metrics */}
        {marketMetrics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Market Gainers</p>
                  <p className="text-3xl font-bold text-green-600">
                    {marketMetrics.gainers || 0}
                  </p>
                </div>
                <ArrowUp className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Market Losers</p>
                  <p className="text-3xl font-bold text-red-600">
                    {marketMetrics.losers || 0}
                  </p>
                </div>
                <ArrowDown className="w-8 h-8 text-red-500" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Stocks</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {marketMetrics.totalStocks || 0}
                  </p>
                </div>
                <Activity className="w-8 h-8 text-blue-500" />
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Enhanced Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Enhanced Stock List */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {searchTerm.trim() ? "Search Results" : "Market Stocks"}
                {displayStocks.length > 0 && (
                  <span className="text-sm text-gray-500 ml-2">
                    ({displayStocks.length})
                  </span>
                )}
              </h3>
              <div className="space-y-3 max-h-8px overflow-y-auto">
                {displayStocks.length > 0 ? (
                  displayStocks.map((stock) => (
                    <div
                      key={stock.productId}
                      className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                        selectedStock?.productId === stock.productId
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-blue-300"
                      }`}
                      onClick={() => handleStockSelection(stock)} // FIXED: Use new handler
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            {stock.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {stock.symbol} • {stock.sector}
                          </p>
                        </div>
                        <div className="flex items-center ml-2">
                          {getTrendIcon(stock.trend)}
                          <span
                            className={`ml-1 text-xs ${getTrendStrengthColor(
                              stock.trendStrength
                            )}`}
                          >
                            {stock.trendStrength}%
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-gray-900">
                          {formatCurrency(stock.currentPrice)}
                        </span>
                        <div className="text-right">
                          <span
                            className={`text-sm font-medium ${
                              stock.change >= 0
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {stock.change >= 0 ? "+" : ""}
                            {stock.percentage?.toFixed(2)}%
                          </span>
                          <div className="text-xs text-gray-500">
                            {formatCurrency(stock.change)}
                          </div>
                        </div>
                      </div>

                      {/* Enhanced metrics */}
                      {stock.volatility && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">
                              Vol: {stock.volatility}%
                            </span>
                            <span className="text-gray-500">
                              52W: {stock.position52Week?.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Search className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">
                      {searchTerm.trim()
                        ? "No stocks found"
                        : "No data available"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            {/* Selected Stock Header */}
            {selectedStock && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {selectedStock.name}
                      </h2>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-sm text-gray-500">
                          {selectedStock.symbol}
                        </span>
                        <span className="text-sm text-gray-500">•</span>
                        <span className="text-sm text-gray-500">
                          {selectedStock.sector}
                        </span>
                        <div className="flex items-center ml-2">
                          {getTrendIcon(selectedStock.trend, 20)}
                          <span
                            className={`ml-1 text-sm font-medium ${getTrendStrengthColor(
                              selectedStock.trendStrength
                            )}`}
                          >
                            {selectedStock.trendStrength}% {selectedStock.trend}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900">
                      {formatCurrency(selectedStock.currentPrice)}
                    </div>
                    <div className="flex items-center justify-end space-x-2">
                      <span
                        className={`text-lg font-semibold ${
                          selectedStock.change >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {selectedStock.change >= 0 ? "+" : ""}
                        {formatCurrency(selectedStock.change)}
                      </span>
                      <span
                        className={`text-sm ${
                          selectedStock.change >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        ({selectedStock.change >= 0 ? "+" : ""}
                        {selectedStock.percentage?.toFixed(2)}%)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Enhanced Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">Market Cap</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {formatLargeNumber(selectedStock.marketCap)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">Volume</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {formatLargeNumber(selectedStock.volume)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">Volatility</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {selectedStock.volatility?.toFixed(2)}%
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">52W Position</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {selectedStock.position52Week?.toFixed(1)}%
                    </div>
                  </div>
                </div>

                {/* 52-Week Range Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>
                      52W Low: {formatCurrency(selectedStock.low52Week)}
                    </span>
                    <span>
                      52W High: {formatCurrency(selectedStock.high52Week)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${selectedStock.position52Week || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            {/* Chart Controls */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Price Chart
                </h3>
                <div className="flex items-center space-x-4">
                  {/* Time Frame Selector */}
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Timeframe:</span>
                    <select
                      value={timeFrame}
                      onChange={(e) => setTimeFrame(e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="1d">1D</option>
                      <option value="1w">1W</option>
                      <option value="1m">1M</option>
                      <option value="3m">3M</option>
                      <option value="6m">6M</option>
                      <option value="1y">1Y</option>
                    </select>
                  </div>

                  {/* Chart Type Selector */}
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Chart:</span>
                    <select
                      value={chartType}
                      onChange={(e) => setChartType(e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="line">Line</option>
                      <option value="area">Area</option>
                      <option value="candlestick">Candlestick</option>
                    </select>
                  </div>

                  <button
                    onClick={() =>
                      selectedStock &&
                      fetchStockHistory(selectedStock.productId)
                    }
                    className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    disabled={isLoading}
                  >
                    <RefreshCw
                      className={`w-4 h-4 mr-1 ${
                        isLoading ? "animate-spin" : ""
                      }`}
                    />
                    Refresh
                  </button>
                </div>
              </div>

              {/* Chart Container */}
              <div className="h-96 w-full">
                {stockHistory && stockHistory.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    {renderAdvancedChart()}
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
                    {isLoading ? (
                      <div className="text-center">
                        <Loader
                          className="animate-spin text-blue-500 mb-2 mx-auto"
                          size={32}
                        />
                        <p className="text-gray-600">Loading chart data...</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <BarChart2
                          className="text-gray-400 mb-2 mx-auto"
                          size={32}
                        />
                        <p className="text-gray-600">
                          {selectedStock
                            ? "No chart data available"
                            : "Select a stock to view chart"}
                        </p>
                        {selectedStock && (
                          <button
                            onClick={() =>
                              fetchStockHistory(selectedStock.productId)
                            }
                            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Load Chart Data
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedStockDashboard;
